/**
 * Awaited generated-profile auto-import for the Blueprint profile panel (ADR-05x).
 *
 * Calls `POST /api/documents/auto-import-profile`, AWAITED, once per patient
 * view, and again on an explicit refresh. Mirrors {@link usePopulateSources}
 * exactly — the same "extend, don't duplicate" reuse this feature was built
 * on: an awaited on-view call with its own loading flag, never a
 * fire-and-forget background trigger (which failed silently under Cloud Run
 * `cpu-throttling=true` for the sources feature this pattern was born from).
 *
 * **Most patients have no generated profile upstream.** That is reported as
 * `status: "not_applicable"` — a quiet, expected outcome, never a failure.
 * `result.imported` is the caller's signal to reload the profile list.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";

const AUTO_IMPORT_ENDPOINT = "/api/documents/auto-import-profile";

/** The status the backend reports for one auto-import-profile attempt. */
export type AutoImportProfileStatus =
  | "succeeded"
  | "not_applicable"
  | "failed"
  | "in_progress"
  | "skipped_not_due";

/** The parsed response of an auto-import-profile call. */
export interface AutoImportProfileResult {
  imported: boolean;
  status: AutoImportProfileStatus;
  last_error: string | null;
  document_id: string | null;
}

/** The result surface returned by {@link useAutoImportProfile}. */
export interface UseAutoImportProfileResult {
  /** True while the awaited auto-import call is in flight. */
  importing: boolean;
  /** The most recent completed result, or null before any call resolves. */
  result: AutoImportProfileResult | null;
  /** Explicitly (re-)run the check, bypassing the staleness window. */
  refresh: () => void;
}

/**
 * Check for and import a patient's generated profile on view, awaited.
 *
 * @param patientId - The active patient id.
 * @returns The loading flag, the last result, and an explicit refresh action.
 */
export function useAutoImportProfile(
  patientId: string,
): UseAutoImportProfileResult {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<AutoImportProfileResult | null>(null);
  const [token, setToken] = useState(0);
  const forceRef = useRef(false);

  useEffect(() => {
    let active = true;
    const isRefresh = forceRef.current;
    forceRef.current = false;

    async function run(): Promise<void> {
      setImporting(true);
      setResult(null);
      try {
        const resp = await apiUtils.fetchData<AutoImportProfileResult>(
          AUTO_IMPORT_ENDPOINT,
          "POST",
          { patient_id: patientId, refresh: isRefresh },
        );
        if (!active) return;
        if (isApiError(resp)) {
          logger.devOnly.warn("useAutoImportProfile: check failed");
          setResult({
            imported: false,
            status: "failed",
            last_error: resp.error,
            document_id: null,
          });
          return;
        }
        setResult(resp);
      } finally {
        if (active) setImporting(false);
      }
    }

    run().catch((err: unknown) => {
      if (!active) return;
      logger.devOnly.warn("useAutoImportProfile: check threw", {
        error: String(err),
      });
      setResult({
        imported: false,
        status: "failed",
        last_error: err instanceof Error ? err.message : String(err),
        document_id: null,
      });
      setImporting(false);
    });

    return () => {
      active = false;
    };
  }, [patientId, token]);

  const refresh = useCallback((): void => {
    forceRef.current = true;
    setToken((t) => t + 1);
  }, []);

  return { importing, result, refresh };
}

export default useAutoImportProfile;
